import { useState, useEffect, useCallback, useReducer } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getConnection, startConnection } from '../services/signalr'
import { getActivityLog } from '../services/api'
import { LIVE_OPERATIONS_KEY, useLiveOperations } from './useLiveOperations'
import type {
  EtlJobStartedPayload,
  OrchestratorStartedPayload,
  EntityEtlStartedPayload,
  EtlProgressPayload,
  EtlJobCompletedPayload,
  ActivityLogEntry,
} from '../types/api'
import type { LiveOperation, LiveOperations, LiveRun } from '../types/liveOperations'
import type { StaleRule } from '../types/etlProgress'
import type { EtlCompletedPayload, EtlFailedPayload, OrchestratorCompletedPayload } from './useSignalR'
import { NOT_MEASURED, exactNumber, shortDuration } from '../utils/format'

/**
 * Live Activity's state: what is running comes from the SERVER, and the hub only enriches it.
 *
 * This hook used to latch `isRunning` from any hub event and clear it only on `EtlJobCompleted`, which
 * only Caravan.Schedule's EtlJobRunner publishes and the live ELT path never reaches. The first real
 * EtlProgress tick therefore pinned the running banner open until a reload. Now:
 * - running / stale / idle is `api/observatory/operations/live` (the shared ['observatory','liveOperations']
 *   query, polled on LIVE), decided with the worker's own stale rule as served — nothing here invents an
 *   idle timeout from the browser clock;
 * - hub events add what only they carry (a job type, a module's counters, rows so far in an attempt)
 *   and ask the server again, at most once per LIVE_INVALIDATE_THROTTLE_MS;
 * - whenever the server answers that nothing is in flight, the hub-derived state is dropped, so an old
 *   run's counters cannot decorate the next run's banner.
 */

export interface ActiveJob {
  jobType: string
  wasReset: boolean
  moduleName: string | null
  entityName: string | null
  totalModules: number
  startedAt: string
}

export interface ActiveModule {
  orchestratorName: string
  totalEntities: number
  completedEntities: number
  wasReset: boolean
  startedAt: string
  currentEntity: string | null
  currentPhase: string | null
  currentRecordsProcessed: number
}

export interface ActiveEntity {
  entityName: string
  module: string | null
  wasReset: boolean
  startedAt: string
  phase: string
  recordsProcessed: number
  totalRecords: number | null
  currentPage: number
}

export type TimelineEntryType =
  | 'job-started'
  | 'job-completed'
  | 'orchestrator-started'
  | 'orchestrator-completed'
  | 'entity-started'
  | 'entity-completed'
  | 'entity-failed'
  | 'progress'

export interface TimelineEntry {
  id: string
  type: TimelineEntryType
  title: string
  description: string
  timestamp: string
  success?: boolean
  module?: string | null
}

let timelineIdCounter = 0
function nextTimelineId(): string {
  timelineIdCounter += 1
  return `tl-${timelineIdCounter}-${Date.now()}`
}

// ---------------------------------------------------------------------------
// Served state: running, stale or idle
// ---------------------------------------------------------------------------

/** Hub events ask the server again at most this often. The poll itself is LIVE (10 s). */
export const LIVE_INVALIDATE_THROTTLE_MS = 5_000

/**
 * The session's event stream keeps only this many entries. EtlProgress arrives up to once a second per
 * entity (EtlProgressReporter's throttle), so a warehouse load would otherwise grow the list without end.
 */
export const MAX_TIMELINE_ENTRIES = 500

/** Said when the cache holds something that is not a live-operations answer. */
export const UNREADABLE_LIVE_ANSWER =
  'The live-operations answer was not in the shape this page reads, so what is running is not known.'

export interface RunningStatus {
  kind: 'running'
  /** The server's instant every stale flag was computed against. */
  observedAt: string
  /** Browser-clock instant the answer arrived; only ever used as a delta, to tick an age forward. */
  receivedAt: number
  /** Served entity runs that are Running and NOT stale (from a list capped at 50). */
  liveOperations: LiveOperation[]
  /** Uncapped: Running entity runs less the stale ones. */
  liveOperationCount: number
  /** Served runs that are Running and NOT stale. */
  liveRuns: LiveRun[]
  /** Served: runs Running and not stale. A crashed worker's run is never counted here. */
  liveRunCount: number
  staleOperationCount: number
  staleRunCount: number
  staleRule: StaleRule
}

export interface StaleStatus {
  kind: 'stale'
  observedAt: string
  staleRule: StaleRule
  /** Uncapped count of entity runs recorded as Running whose heartbeat is older than the rule. */
  staleOperationCount: number
  /** Runs recorded as Running whose newest heartbeat is older than the rule. */
  staleRunCount: number
  /** The oldest last sign of life across the stale rows; null when the server did not measure one. */
  oldestStaleSince: string | null
  /** The server's remark on why the count can include months-old rows. */
  staleBasis: string
}

export type LiveStatus =
  | { kind: 'loading' }
  | { kind: 'unavailable'; error: unknown }
  | { kind: 'idle'; observedAt: string }
  | RunningStatus
  | StaleStatus

export interface LiveOperationsQueryState {
  data: unknown
  isLoading: boolean
  isError: boolean
  error: unknown
  dataUpdatedAt: number
}

function isLiveOperations(value: unknown): value is LiveOperations {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false
  const v = value as Partial<Record<keyof LiveOperations, unknown>>
  const rule = v.staleRule as Partial<StaleRule> | null | undefined
  return Array.isArray(v.operations)
    && Array.isArray(v.runs)
    && typeof v.operationCount === 'number'
    && typeof v.staleOperationCount === 'number'
    && typeof v.liveRunCount === 'number'
    && typeof v.observedAt === 'string'
    && rule != null && typeof rule.minutes === 'number'
}

/**
 * The uncapped count of stale runs: the served `staleRunCount` (the list carries only the newest 50
 * stale runs). An API that predates the field listed every Running run, so the listed count is then
 * exact — which is why the field is still checked at run time.
 */
function staleRunCountOf(data: LiveOperations): number {
  return typeof data.staleRunCount === 'number' ? data.staleRunCount : data.runs.filter((r) => r.isStale).length
}

/**
 * What the served state says is in flight.
 *
 * Running is decided ONLY from non-stale work: an entity run the server does not flag stale, or
 * `liveRunCount` (runs Running and not stale). A run left Running by a crashed worker stays Running in
 * the table for all time, so "a run is listed" would re-create the latch this replaces — that case is
 * `stale`, shown as a warning rather than as running. A failed read is `unavailable`, never idle: not
 * knowing is not the same finding as nothing running.
 */
export function deriveLiveStatus(query: LiveOperationsQueryState): LiveStatus {
  if (query.isError) return { kind: 'unavailable', error: query.error }
  if (query.isLoading || query.data == null) return { kind: 'loading' }
  if (!isLiveOperations(query.data)) return { kind: 'unavailable', error: new Error(UNREADABLE_LIVE_ANSWER) }

  const data = query.data
  const liveOperations = data.operations.filter((o) => !o.isHeartbeatStale)
  // The list is capped at 50, newest first, and stale rows accumulate: the uncapped counts decide.
  const liveOperationCount = Math.max(liveOperations.length, data.operationCount - data.staleOperationCount)
  const staleRunCount = staleRunCountOf(data)

  if (liveOperationCount > 0 || data.liveRunCount > 0) {
    return {
      kind: 'running',
      observedAt: data.observedAt,
      receivedAt: query.dataUpdatedAt,
      liveOperations,
      liveOperationCount,
      liveRuns: data.runs.filter((r) => !r.isStale),
      liveRunCount: data.liveRunCount,
      staleOperationCount: data.staleOperationCount,
      staleRunCount,
      staleRule: data.staleRule,
    }
  }

  const anyStale = data.staleOperationCount > 0
    || staleRunCount > 0
    || data.operations.some((o) => o.isHeartbeatStale)
    || data.runs.some((r) => r.isStale)
  if (anyStale) {
    return {
      kind: 'stale',
      observedAt: data.observedAt,
      staleRule: data.staleRule,
      staleOperationCount: data.staleOperationCount,
      staleRunCount,
      oldestStaleSince: data.oldestStaleSince,
      staleBasis: data.staleBasis,
    }
  }

  return { kind: 'idle', observedAt: data.observedAt }
}

/** The stale variant's headline, in the worker's own threshold. */
export function staleHeadline(rule: StaleRule): string {
  return `Recorded as running, but no heartbeat for more than ${rule.minutes} min`
}

/**
 * Seconds since `startedAt`, measured on the SERVER's clock (`observedAt`) and ticked forward by how long
 * ago, on the browser's clock, the answer arrived. Null when either instant is missing or unreadable.
 */
export function elapsedSince(
  startedAt: string | null,
  observedAt: string,
  receivedAt: number,
  now: number | null,
): number | null {
  if (startedAt == null) return null
  const start = Date.parse(startedAt)
  const observed = Date.parse(observedAt)
  if (Number.isNaN(start) || Number.isNaN(observed)) return null
  const sinceAnswer = now == null || receivedAt <= 0 ? 0 : Math.max(0, now - receivedAt)
  return Math.max(0, Math.floor((observed - start + sinceAnswer) / 1000))
}

// ---------------------------------------------------------------------------
// Hub-derived enrichment
// ---------------------------------------------------------------------------

export interface HubState {
  /** Only EtlJobRunner's EtlJobStarted sets this; the live ELT path publishes none, so it is usually null. */
  job: ActiveJob | null
  modules: Map<string, ActiveModule>
  entities: Map<string, ActiveEntity>
}

export const EMPTY_HUB: HubState = { job: null, modules: new Map(), entities: new Map() }

export type HubAction =
  | { type: 'jobStarted'; data: EtlJobStartedPayload }
  | { type: 'orchestratorStarted'; data: OrchestratorStartedPayload }
  | { type: 'entityStarted'; data: EntityEtlStartedPayload }
  | { type: 'progress'; data: EtlProgressPayload }
  | { type: 'entityFinished'; data: { entityName: string; module: string | null } }
  | { type: 'orchestratorCompleted'; data: OrchestratorCompletedPayload }
  | { type: 'reset' }

/** The module entry an entity event belongs to (the existing name-containment rule), or null. */
function moduleKeyFor(modules: Map<string, ActiveModule>, module: string | null): string | null {
  if (!module) return null
  const needle = module.toLowerCase().replace(/\s+/g, '')
  for (const [key, mod] of modules) {
    if (mod.orchestratorName.toLowerCase().includes(needle)) return key
  }
  return null
}

function withModule(
  modules: Map<string, ActiveModule>,
  module: string | null,
  update: (mod: ActiveModule) => ActiveModule,
): Map<string, ActiveModule> {
  const key = moduleKeyFor(modules, module)
  if (key == null) return modules
  const next = new Map(modules)
  next.set(key, update(modules.get(key)!))
  return next
}

export function hubReducer(state: HubState, action: HubAction): HubState {
  switch (action.type) {
    case 'reset':
      return state === EMPTY_HUB ? state : EMPTY_HUB
    case 'jobStarted': {
      const d = action.data
      return {
        job: {
          jobType: d.jobType,
          wasReset: d.wasReset,
          moduleName: d.moduleName,
          entityName: d.entityName,
          totalModules: d.totalModules,
          startedAt: d.timestamp,
        },
        modules: new Map(),
        entities: new Map(),
      }
    }
    case 'orchestratorStarted': {
      const d = action.data
      const modules = new Map(state.modules)
      modules.set(d.orchestratorName, {
        orchestratorName: d.orchestratorName,
        totalEntities: d.totalEntities,
        completedEntities: 0,
        wasReset: d.wasReset,
        startedAt: d.timestamp,
        currentEntity: null,
        currentPhase: null,
        currentRecordsProcessed: 0,
      })
      return { ...state, modules }
    }
    case 'entityStarted': {
      const d = action.data
      const entities = new Map(state.entities)
      entities.set(d.entityName, {
        entityName: d.entityName,
        module: d.module,
        wasReset: d.wasReset,
        startedAt: d.timestamp,
        phase: 'Extract',
        recordsProcessed: 0,
        totalRecords: null,
        currentPage: 0,
      })
      const modules = withModule(state.modules, d.module, (mod) => ({
        ...mod, currentEntity: d.entityName, currentPhase: 'Extract', currentRecordsProcessed: 0,
      }))
      return { ...state, modules, entities }
    }
    case 'progress': {
      const d = action.data
      const entities = new Map(state.entities)
      const existing = entities.get(d.entityName)
      entities.set(d.entityName, existing
        ? { ...existing, phase: d.phase, recordsProcessed: d.recordsProcessed, totalRecords: d.totalRecords, currentPage: d.currentPage }
        : {
          // Mid-stream join: the entity's start was before this session.
          entityName: d.entityName,
          module: d.module,
          wasReset: false,
          startedAt: d.timestamp,
          phase: d.phase,
          recordsProcessed: d.recordsProcessed,
          totalRecords: d.totalRecords,
          currentPage: d.currentPage,
        })
      const modules = withModule(state.modules, d.module, (mod) => ({
        ...mod, currentEntity: d.entityName, currentPhase: d.phase, currentRecordsProcessed: d.recordsProcessed,
      }))
      return { ...state, modules, entities }
    }
    case 'entityFinished': {
      const d = action.data
      const entities = new Map(state.entities)
      entities.delete(d.entityName)
      const modules = withModule(state.modules, d.module, (mod) => ({
        ...mod,
        completedEntities: mod.completedEntities + 1,
        currentEntity: null,
        currentPhase: null,
        currentRecordsProcessed: 0,
      }))
      return { ...state, modules, entities }
    }
    case 'orchestratorCompleted': {
      const existing = state.modules.get(action.data.orchestratorName)
      if (!existing) return state
      // Marked complete rather than removed, so its entity totals stay readable.
      const modules = new Map(state.modules)
      modules.set(action.data.orchestratorName, {
        ...existing,
        completedEntities: existing.totalEntities,
        currentEntity: null,
        currentPhase: null,
        currentRecordsProcessed: 0,
      })
      return { ...state, modules }
    }
  }
}

/**
 * The served answer the hub state must be dropped for, or null to keep it: every answer that says
 * nothing is in flight (idle, or only stale work). Keyed by the answer's arrival so each such answer
 * resets once, and hub events heard before the server catches up with a new run are kept until then.
 */
export function hubResetStamp(status: LiveStatus, receivedAt: number): number | null {
  return status.kind === 'idle' || status.kind === 'stale' ? receivedAt : null
}

/** Appends to the session's event stream, keeping the newest MAX_TIMELINE_ENTRIES. */
export function appendTimeline(prev: TimelineEntry[], entry: TimelineEntry): TimelineEntry[] {
  const next = [...prev, entry]
  return next.length > MAX_TIMELINE_ENTRIES ? next.slice(next.length - MAX_TIMELINE_ENTRIES) : next
}

// ---------------------------------------------------------------------------
// The running banner's view: served rows, enriched
// ---------------------------------------------------------------------------

export interface BannerEntity {
  entityRunId: string
  executionId: string
  entityName: string
  module: string | null
  /** Served (the newest chunk): "Landing chunk 3"; null when no chunk is landing or transforming. */
  phase: string | null
  /** Rows so far in this attempt, from the hub's EtlProgress; null when this session heard none. */
  rowsThisAttempt: number | null
}

export interface RunCardView {
  executionId: string
  runKey: string
  orchestratorName: string
  module: string | null
  entitiesFinished: number
  entitiesRunning: number
  entitiesStale: number
  /** Null when a resume mixed job kinds, so the recorded total no longer describes the rows. */
  entitiesNotStarted: number | null
  countsMixResumedJobs: boolean
  /** Served live entity runs of this run, enriched like the banner's. */
  current: BannerEntity[]
}

export interface RunningView {
  /** From EtlJobStarted only; null when this session heard none — never an invented "Unknown". */
  jobType: string | null
  wasReset: boolean
  liveOperationCount: number
  liveRunCount: number
  entities: BannerEntity[]
  /** Live entity runs the capped list did not carry. */
  entitiesNotListed: number
  /** The earliest start among the live runs (else the live entity runs); null when none recorded one. */
  startedAt: string | null
  /**
   * A live entity run was resumed (attempted more than once), or a resume mixed job kinds into a live run.
   * A resume reopens the run WITHOUT resetting its StartedAt, so the banner's timer then measures from the
   * run's first start — possibly days ago — and has to say so.
   */
  resumed: boolean
  observedAt: string
  receivedAt: number
  staleOperationCount: number
  staleRunCount: number
  staleRule: StaleRule
  runs: RunCardView[]
}

/** "Landing chunk 3" from the served phase, the way the Explorer says it; null without one. */
export function servedPhaseText(op: Pick<LiveOperation, 'livePhase' | 'liveChunkIndex'>): string | null {
  if (op.liveChunkIndex == null) return null
  if (op.livePhase === 'landing') return `Landing chunk ${op.liveChunkIndex + 1}`
  if (op.livePhase === 'transforming') return `Transforming chunk ${op.liveChunkIndex + 1}`
  return null
}

function hubEntityFor(hub: HubState, entityName: string): ActiveEntity | null {
  const exact = hub.entities.get(entityName)
  if (exact) return exact
  const wanted = entityName.toLowerCase()
  for (const entity of hub.entities.values()) {
    if (entity.entityName.toLowerCase() === wanted) return entity
  }
  return null
}

function earliest(instants: (string | null)[]): string | null {
  let best: string | null = null
  let bestMs = Number.POSITIVE_INFINITY
  for (const iso of instants) {
    if (iso == null) continue
    const ms = Date.parse(iso)
    if (!Number.isNaN(ms) && ms < bestMs) {
      best = iso
      bestMs = ms
    }
  }
  return best
}

/**
 * What the running banner and the run cards show. Every row is a SERVED live row; the hub only adds
 * counters to rows the server lists, so a hub entry the server does not list as live is never shown.
 */
export function runningView(status: RunningStatus, hub: HubState): RunningView {
  const entities = status.liveOperations.map((op): BannerEntity => ({
    entityRunId: op.entityRunId,
    executionId: op.executionId,
    entityName: op.entityName,
    module: op.module,
    phase: servedPhaseText(op),
    rowsThisAttempt: hubEntityFor(hub, op.entityName)?.recordsProcessed ?? null,
  }))
  const runs = status.liveRuns.map((run): RunCardView => ({
    executionId: run.executionId,
    runKey: run.runKey,
    orchestratorName: run.orchestratorName,
    module: run.module,
    entitiesFinished: run.entitiesFinished,
    entitiesRunning: run.entitiesRunning,
    entitiesStale: run.entitiesStale,
    entitiesNotStarted: run.countsMixResumedJobs ? null : run.entitiesNotStarted,
    countsMixResumedJobs: run.countsMixResumedJobs,
    current: entities.filter((e) => e.executionId === run.executionId),
  }))
  return {
    jobType: hub.job?.jobType ?? null,
    wasReset: hub.job?.wasReset ?? false,
    liveOperationCount: status.liveOperationCount,
    liveRunCount: status.liveRunCount,
    entities,
    entitiesNotListed: Math.max(0, status.liveOperationCount - entities.length),
    startedAt: earliest(status.liveRuns.map((r) => r.startedAt))
      ?? earliest(status.liveOperations.map((o) => o.startedAt)),
    resumed: status.liveOperations.some((o) => (o.attemptCount ?? 1) > 1)
      || status.liveRuns.some((r) => r.countsMixResumedJobs),
    observedAt: status.observedAt,
    receivedAt: status.receivedAt,
    staleOperationCount: status.staleOperationCount,
    staleRunCount: status.staleRunCount,
    staleRule: status.staleRule,
    runs,
  }
}

/**
 * The banner's timer. A resumed run keeps its first StartedAt (EtlRunTelemetry reopens it as Running
 * with CompletedAt cleared), so after a resume the time is since the run was FIRST started, and a
 * run resumed a minute ago may read hours — said in the words, not left for a reader to misread.
 */
export function runningTimerText(elapsedSeconds: number | null, resumed: boolean): string {
  if (elapsedSeconds == null) return NOT_MEASURED
  return resumed
    ? `${shortDuration(elapsedSeconds)} since the run was first started (resumed)`
    : `Running for ${shortDuration(elapsedSeconds)}`
}

/** How many activity rows Job History asks for. The request and the caption read this one constant. */
export const HISTORY_PAGE_SIZE = 50

/**
 * Job History's caption: what is shown of how much is on file. The list is one page of the log, so a
 * page that holds less than the served total says it is the newest N — never a silent cap.
 */
export function historyCaption(shown: number | null, total: number | null): string {
  const base = 'Persisted ETL activity from the data warehouse'
  if (shown == null || total == null || total === 0) return base
  return total > shown
    ? `${base} — the newest ${exactNumber(shown)} of ${exactNumber(total)}`
    : `${base} — all ${exactNumber(total)}`
}

// ---------------------------------------------------------------------------
// Throttle
// ---------------------------------------------------------------------------

export interface Throttle {
  /** Runs now if the last run was at least `intervalMs` ago, else once when the interval ends. */
  call(): void
  cancel(): void
}

/**
 * At most one run per `intervalMs`, leading edge first; a call inside the interval schedules ONE
 * trailing run at its end, so the last event of a burst is never lost for longer than the interval.
 */
export function createThrottle(run: () => void, intervalMs: number, now: () => number = Date.now): Throttle {
  let last = Number.NEGATIVE_INFINITY
  let timer: ReturnType<typeof setTimeout> | null = null
  const fire = () => {
    timer = null
    last = now()
    run()
  }
  return {
    call() {
      if (timer !== null) return
      const wait = last + intervalMs - now()
      if (wait <= 0) fire()
      else timer = setTimeout(fire, wait)
    },
    cancel() {
      if (timer !== null) clearTimeout(timer)
      timer = null
    },
  }
}

// ---------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------

export function useEtlActivity() {
  const queryClient = useQueryClient()
  const liveOps = useLiveOperations()
  const [hub, dispatch] = useReducer(hubReducer, EMPTY_HUB)
  const [timeline, setTimeline] = useState<TimelineEntry[]>([])
  const [resetFor, setResetFor] = useState<number | null>(null)

  const addTimelineEntry = useCallback((entry: Omit<TimelineEntry, 'id'>) => {
    setTimeline((prev) => appendTimeline(prev, { ...entry, id: nextTimelineId() }))
  }, [])

  // Historical activity from the API. isError is carried out: a 401 or a 500 is not "no activity".
  const historyQuery = useQuery({
    queryKey: ['etlActivity', 'history'],
    queryFn: () => getActivityLog(1, HISTORY_PAGE_SIZE),
    refetchInterval: 30000,
  })

  const live = deriveLiveStatus({
    data: liveOps.data,
    isLoading: liveOps.isLoading,
    isError: liveOps.isError,
    error: liveOps.error,
    dataUpdatedAt: liveOps.dataUpdatedAt,
  })

  // The server says nothing is in flight: drop what the hub told this session. Done while rendering
  // (React's "adjusting state when a prop changes"), once per such answer, rather than in an effect.
  const stamp = hubResetStamp(live, liveOps.dataUpdatedAt)
  if (stamp !== null && stamp !== resetFor) {
    setResetFor(stamp)
    dispatch({ type: 'reset' })
  }

  useEffect(() => {
    let mounted = true
    const connection = getConnection()
    const askServer = createThrottle(
      () => { void queryClient.invalidateQueries({ queryKey: LIVE_OPERATIONS_KEY }) },
      LIVE_INVALIDATE_THROTTLE_MS,
    )

    const onJobStarted = (data: EtlJobStartedPayload) => {
      if (!mounted) return
      dispatch({ type: 'jobStarted', data })
      askServer.call()
      addTimelineEntry({
        type: 'job-started',
        title: `${data.jobType} Job Started`,
        description: data.wasReset
          ? `Full reset - ${data.totalModules} modules`
          : `Incremental - ${data.totalModules} modules`,
        timestamp: data.timestamp,
        success: true,
      })
    }

    const onOrchestratorStarted = (data: OrchestratorStartedPayload) => {
      if (!mounted) return
      dispatch({ type: 'orchestratorStarted', data })
      askServer.call()
      addTimelineEntry({
        type: 'orchestrator-started',
        title: `${data.orchestratorName} Started`,
        description: `Processing ${data.totalEntities} entities${data.wasReset ? ' (reset)' : ''}`,
        timestamp: data.timestamp,
        module: data.orchestratorName,
      })
    }

    const onEntityStarted = (data: EntityEtlStartedPayload) => {
      if (!mounted) return
      dispatch({ type: 'entityStarted', data })
      askServer.call()
      addTimelineEntry({
        type: 'entity-started',
        title: `${data.entityName} Started`,
        description: `Module: ${data.module ?? 'N/A'}`,
        timestamp: data.timestamp,
        module: data.module,
      })
    }

    const onProgress = (data: EtlProgressPayload) => {
      if (!mounted) return
      dispatch({ type: 'progress', data })
      askServer.call()
      const total = data.totalRecords != null ? `/${data.totalRecords.toLocaleString()}` : ''
      addTimelineEntry({
        type: 'progress',
        title: `${data.entityName} Progress`,
        // currentPage carries ChunkedEltRunner's zero-based chunk index, the only progress reporter.
        description: `${data.phase}: ${data.recordsProcessed.toLocaleString()}${total} records (chunk ${data.currentPage + 1})`,
        timestamp: data.timestamp,
        module: data.module,
      })
    }

    // Completions are left to useSignalR, which invalidates the live query (and the rest) unthrottled.
    const onEtlCompleted = (data: EtlCompletedPayload) => {
      if (!mounted) return
      dispatch({ type: 'entityFinished', data })
      addTimelineEntry({
        type: 'entity-completed',
        title: `${data.entityName} Completed`,
        description: `Loaded ${data.loadedCount.toLocaleString()} records in ${data.duration.toFixed(1)}s`,
        timestamp: data.timestamp,
        success: true,
        module: data.module,
      })
    }

    const onEtlFailed = (data: EtlFailedPayload) => {
      if (!mounted) return
      dispatch({ type: 'entityFinished', data })
      addTimelineEntry({
        type: 'entity-failed',
        title: `${data.entityName} Failed`,
        description: `Failed during ${data.failedPhase}: ${data.errorMessage}`,
        timestamp: data.timestamp,
        success: false,
        module: data.module,
      })
    }

    const onOrchestratorCompleted = (data: OrchestratorCompletedPayload) => {
      if (!mounted) return
      dispatch({ type: 'orchestratorCompleted', data })
      addTimelineEntry({
        type: 'orchestrator-completed',
        title: `${data.orchestratorName} Completed`,
        description: `${data.successCount}/${data.entitiesProcessed} entities, ${data.totalRecordsLoaded.toLocaleString()} records in ${data.duration.toFixed(1)}s`,
        timestamp: data.timestamp,
        success: data.failureCount === 0,
        module: data.module,
      })
    }

    // Kept for the day EtlJobCompleted is fanned out on the live path; today only EtlJobRunner sends it.
    const onJobCompleted = (data: EtlJobCompletedPayload) => {
      if (!mounted) return
      dispatch({ type: 'reset' })
      addTimelineEntry({
        type: 'job-completed',
        title: `${data.jobType} Job Completed`,
        description: data.failureCount > 0
          ? `${data.totalEntitiesProcessed} entities, ${data.totalRecordsLoaded.toLocaleString()} records, ${data.failureCount} failures in ${data.duration.toFixed(1)}s`
          : `${data.totalEntitiesProcessed} entities, ${data.totalRecordsLoaded.toLocaleString()} records in ${data.duration.toFixed(1)}s`,
        timestamp: data.timestamp,
        success: data.failureCount === 0,
      })
    }

    connection.on('EtlJobStarted', onJobStarted)
    connection.on('OrchestratorStarted', onOrchestratorStarted)
    connection.on('EntityEtlStarted', onEntityStarted)
    connection.on('EtlProgress', onProgress)
    connection.on('EtlCompleted', onEtlCompleted)
    connection.on('EtlFailed', onEtlFailed)
    connection.on('OrchestratorCompleted', onOrchestratorCompleted)
    connection.on('EtlJobCompleted', onJobCompleted)

    // Ensure the connection is started (safe to call multiple times)
    startConnection()

    return () => {
      mounted = false
      askServer.cancel()
      connection.off('EtlJobStarted', onJobStarted)
      connection.off('OrchestratorStarted', onOrchestratorStarted)
      connection.off('EntityEtlStarted', onEntityStarted)
      connection.off('EtlProgress', onProgress)
      connection.off('EtlCompleted', onEtlCompleted)
      connection.off('EtlFailed', onEtlFailed)
      connection.off('OrchestratorCompleted', onOrchestratorCompleted)
      connection.off('EtlJobCompleted', onJobCompleted)
    }
  }, [addTimelineEntry, queryClient])

  const history: ActivityLogEntry[] | undefined = historyQuery.data?.data

  return {
    /** Served: running, stale-only, idle, still loading, or not readable. */
    live,
    /** True only for non-stale served work. */
    isRunning: live.kind === 'running',
    /** Only stale work: recorded as Running, but past the worker's heartbeat rule. */
    isStale: live.kind === 'stale',
    /** The running banner's rows, or null when nothing is live. */
    running: live.kind === 'running' ? runningView(live, hub) : null,
    timeline,
    /** Undefined until the history is served. */
    history,
    /** Every activity row on file (the served totalCount), or null until served — the caption's "of N". */
    historyTotal: historyQuery.data?.totalCount ?? null,
    historyLoading: historyQuery.isLoading,
    /** Null unless the history read failed. */
    historyError: historyQuery.isError ? (historyQuery.error ?? new Error('The activity history could not be read.')) : null,
  }
}
